import React from 'react';
import { InfoBox, ExerciseBox, CodeBlock, InlineCode, Result, SectionTitle, Image, Separator, CorrectionBox, Note, TableContainer, Highlight, PatternContainer, SectionHeading, SubHeading, TwoColGrid, ThreeColGrid, GridItem, Divider } from '../../../../components/course/CourseComponents';
import screen5 from '../screen5.jpg';

const Chapitre7Content = () => {
  return (
    <>
      <InfoBox title="Objectif du chapitre" color="indigo">
        <p className="mb-0">
          Détecter les collisions entre l'oiseau et les tuyaux ou les bords de l'écran
        </p>
      </InfoBox>

      <Separator type="text" text="Préambule" color="gray-darker" size="md" />
      <SectionTitle>🎯 1/ Préambule</SectionTitle>

      <p>On s'attaque à la partie qui fait peur : la détection des collisions !<br/>
      Mais finalement tu vas voir, ce n'est vraiment pas compliqué !</p>

      <p><strong>Qu'aimerait-on détecter ?</strong><br/>
      Notre oiseau peut mourir de deux façons : soit il touche un tuyau, soit il touche le haut ou le bas de l'écran.</p>

      <p>Pour ce faire, on va créer une fonction <InlineCode>function VerifierCollision(t)</InlineCode><br/>
      Elle prendra en paramètre une paire de tuyaux <InlineCode>t</InlineCode>, et renverra <InlineCode>true</InlineCode> s'il y a une collision, <InlineCode>false</InlineCode> sinon.<br/>
      Cette fonction sera appelée pour chaque tuyau de la liste <InlineCode>tuyaux</InlineCode>.</p>

      <p>Si la fonction renvoie <InlineCode>true</InlineCode>, alors on affichera un message dans la console :</p>
      <CodeBlock language="javascript">
{`print(frameCount + " COLLISION !");`}
      </CodeBlock>

      <Note type="tip" title="💡 C'est quoi frameCount ?">
        <p>C'est une variable de p5.js qui te donne le nombre d'images (frames) affichées depuis le début du programme.<br/>
        Elle permet de voir dans la console les nouveaux messages qui arrivent.</p>
        <p>Sans ce compteur qui change, tu auras du mal à voir si de nouveaux messages s'affichent dans la console, car ils seront tous identiques.</p>
      </Note>

      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Variable</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>frameCount</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Nombre d'images affichées depuis le début du programme.</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <Note type="exercice" title="🧪 À toi de jouer : création de la fonction VerifierCollision">
        <p>Dans le <InlineCode>for</InlineCode> qui parcourt tous les tuyaux de la liste, appelle la fonction <InlineCode>VerifierCollision</InlineCode> avec le tuyau courant.<br/>
        Si cette fonction renvoie <InlineCode>true</InlineCode>, alors affiche le message "COLLISION !!" avec un compteur :</p>

        <CodeBlock language="javascript">
{`// Vérifie les collisions
if (VerifierCollision(t) == true) {
  print(frameCount + " COLLISION !");
}`}
        </CodeBlock>

        <p>Maintenant, tout en bas du fichier, crée la nouvelle fonction <InlineCode>VerifierCollision</InlineCode> qui renvoie <InlineCode>false</InlineCode>.<br/>
        On verra ensuite comment remplir cette fonction :</p>

        <CodeBlock language="javascript">
{`//--------------------------------- COLLISION ------------------------------------------
function VerifierCollision(t) {
  return false;
}`}
        </CodeBlock>

        <br/>
        <Result>Rien de particulier vu que la fonction renvoie toujours false. Il ne se passe rien de plus qu'avant...</Result>
      </Note>

      <Separator type="text" text="Collisions avec les bords" color="gray-darker" size="md" />
      <SectionTitle>🔝 2/ Les collisions avec le haut et le bas de l'écran</SectionTitle>

      <p>On va commencer par le plus simple : la détection entre l'oiseau et le haut / bas de l'écran.</p>

      <p>Pour cela, il suffit de regarder si :</p>
      <ul>
        <li>la position y de l'oiseau est inférieure à 0 → collision avec le haut de l'écran.</li>
        <li>la position y + taille de l'oiseau est supérieure à 600 → collision avec le bas de l'écran.</li>
      </ul>

      <Note type="tip" title="💡 Pourquoi position y + taille de l'oiseau ?">
        <p>Car le point d'origine (0, 0) de l'image est en haut à gauche.<br/>
        Donc pour savoir si le bas de l'image sort de l'écran, il faut faire position y + taille de l'oiseau.<br/>
        On obtient la taille de l'oiseau avec <InlineCode>imageOiseau.height</InlineCode>.</p>
        <Image src={screen5} alt="Oiseau origine" width="200px" height="160px" borderRadius="xl" shadow={true} border={false} className="object-contain" />
      </Note>

      <Note type="exercice" title="🧪 À toi de jouer : détection des bords haut/bas de l'écran">
        <p>Dans la fonction <InlineCode>VerifierCollision</InlineCode>, vérifie si <InlineCode>oiseau.y &lt; 0</InlineCode><br/>
        ou si <InlineCode>oiseau.y + imageOiseau.height &gt; height</InlineCode>.</p>

        <p>Si c'est le cas, renvoie <InlineCode>true</InlineCode> :</p>

        <CodeBlock language="javascript">
{`// Collision avec le haut de l'écran
if (oiseau.y < 0) {
  return true;
}

// Collision avec le bas de l'écran
if (oiseau.y + imageOiseau.height > height) {
  return true;
}`}
        </CodeBlock>

        <br/>
        <Result>Si l'oiseau touche le haut ou le bas de l'écran, "COLLISION" s'affiche dans la console !</Result>

        <CorrectionBox>
          <CodeBlock language="javascript">
{`//--------------------------------- COLLISION ------------------------------------------
function VerifierCollision(t) {
  // Collision avec le haut de l'écran
  if (oiseau.y < 0) {
    return true;
  }

  // Collision avec le bas de l'écran
  if (oiseau.y + imageOiseau.height > height) {
    return true;
  }

  // Pas de collision
  return false;
}`}
          </CodeBlock>
        </CorrectionBox>
      </Note>

      <Separator type="text" text="Collisions avec les tuyaux" color="gray-darker" size="md" />
      <SectionTitle>🚧 3/ Les collisions avec les tuyaux</SectionTitle>

      <p>Pour détecter les collisions entre l'oiseau et les tuyaux, c'est pas plus compliqué : on veut vérifier si l'oiseau entre dans le rectangle du tuyau.</p>

      <p><strong>Pour le tuyau du haut : voici les conditions à vérifier :</strong></p>

      <ul>
        <li>1️⃣ <InlineCode>oiseau.x + imageOiseau.width &gt; t.x</InlineCode><br/>
        → Le bord droit de l'oiseau dépasse le bord gauche du tuyau ?</li>

        <li>2️⃣ <InlineCode>oiseau.x &lt; t.x + imageTuyauHaut.width</InlineCode><br/>
        → Le bord gauche de l'oiseau est avant le bord droit du tuyau ?</li>

        <li>➡️ Ces deux conditions ensemble signifient :<br/>
        → L'oiseau est dans le tuyau, sur l'axe des X.</li>

        <li>3️⃣ <InlineCode>oiseau.y &lt; t.y</InlineCode><br/>
        → Le haut de l'oiseau est au-dessus du bas du tuyau du haut ?</li>
      </ul>

      <p>✅ Si les 3 sont vraies → Collision avec le tuyau du haut → <InlineCode>return true</InlineCode></p>

      <p><strong>Pour le tuyau du bas :</strong></p>

      <ul>
        <li>1️⃣ <InlineCode>oiseau.x + imageOiseau.width &gt; t.x</InlineCode><br/>
        → Le bord droit de l'oiseau est à droite du bord gauche du tuyau ?</li>

        <li>2️⃣ <InlineCode>oiseau.x &lt; t.x + imageTuyauBas.width</InlineCode><br/>
        → Le bord gauche de l'oiseau est avant le bord droit du tuyau ?</li>

        <li>3️⃣ <InlineCode>oiseau.y + imageOiseau.height &gt; t.y + tuyauEspace</InlineCode><br/>
        → Le bas de l'oiseau est plus bas que le haut du tuyau du bas ?</li>
      </ul>

      <p>✅ Si les 3 sont vraies → Collision avec le tuyau du bas → <InlineCode>return true</InlineCode></p>

      <p>En code, ça donne :</p>

      <CodeBlock language="javascript">
{`// Collision avec le tuyau du haut
if (
  oiseau.x + imageOiseau.width > t.x &&
  oiseau.x < t.x + imageTuyauHaut.width &&
  oiseau.y < t.y
) {
  return true;
}

// Collision avec le tuyau du bas
if (
  oiseau.x + imageOiseau.width > t.x &&
  oiseau.x < t.x + imageTuyauBas.width &&
  oiseau.y + imageOiseau.height > t.y + tuyauEspace
) {
  return true;
}`}
      </CodeBlock>

      <Note type="exercice" title="🧪 À toi de jouer : détection des collisions avec les tuyaux">
        <p>Dans la fonction <InlineCode>VerifierCollision</InlineCode>, ajoute les deux blocs <InlineCode>if</InlineCode> ci-dessus pour détecter si l'oiseau touche un des tuyaux.</p>

        <br/>
        <Result>Quand l'oiseau traverse un tuyau → "COLLISION" s'affiche dans la console !</Result>
      </Note>

      <p><strong>Voilà !</strong></p>

      <p>On sait maintenant quand l'oiseau entre en collision avec le bord de l'écran ou avec les tuyaux !</p>

      <p>🎯 Dans le prochain chapitre, on fera un écran de fin quand une collision est détectée !</p>

      <Separator type="text" text="Code complet" color="gray-darker" size="md" />
      <SectionTitle>✅ 4/ Code complet : Détection des collisions</SectionTitle>

      <CodeBlock language="javascript">
{`// Jeu 3 : Flappy Bird
let imageFond;
let imageTuyauHaut;
let imageTuyauBas;
let imageOiseau;

let tuyauEspace = 130; // Espace entre les tuyaux
let tuyauVitesse = 150; // Vitesse de déplacement des tuyaux
let tuyauIntervalle = 2; // Intervalle entre les tuyaux, en secondes
let oiseauGravite = 800; // Gravité à laquelle l'oiseau tombe
let oiseauImpulsion = -300; // Force d'impulsion quand on clique

let tuyauTemps = 0;
let tuyaux = [];
let score = 0;

// Oiseau
let oiseau = {
  x: 150,
  y: 250,
  vitesseY: 0
};

//--------------------------------- PRELOAD ------------------------------------------
function preload() {
  imageFond = loadImage("fond.png");
  imageTuyauHaut = loadImage("tuyau-haut.png");
  imageTuyauBas = loadImage("tuyau-bas.png");
  imageOiseau = loadImage("flappy1.png");
}

//--------------------------------- SETUP ------------------------------------------
function setup() {
  createCanvas(600, 600);
}

//--------------------------------- DRAW ------------------------------------------
function draw() {
  background(imageFond);
  let dt = deltaTime / 1000;

  // Parcours la liste de tuyaux
  for (let i = tuyaux.length - 1; i >= 0; i--) {
    let t = tuyaux[i];

    // Déplace et affiche le tuyau
    t.x = t.x - tuyauVitesse * dt;
    afficherTuyaux(t);

    // Supprime s'il est complètement à gauche
    if (t.x + imageTuyauHaut.width < 0) {
      tuyaux.splice(i, 1);
    }

    // Vérifie si l’oiseau vient de passer le tuyau
    if (t.franchi == false &&  oiseau.x > t.x) {
      score = score + 1;
      t.franchi = true;
    }

    // Vérifie les collisions
    if (VerifierCollision(t) == true) {
      print(frameCount + " COLLISION !");
    }
  }

  // Créer un nouveau tuyau toutes les x secondes
  tuyauTemps = tuyauTemps - dt;
  if (tuyauTemps <= 0) {
    tuyauTemps = tuyauIntervalle;
    let positionX = width;
    let positionY = random(100, 400);
    tuyaux.push({ x: positionX, y: positionY, franchi: false });
  }

  // Déplacement de l'oiseau
  oiseau.vitesseY = oiseau.vitesseY + oiseauGravite * dt;
  oiseau.y = oiseau.y + oiseau.vitesseY * dt;
  image(imageOiseau, oiseau.x, oiseau.y);

  // Affichage du score
  textAlign(CENTER, TOP);
  textSize(64);
  textStyle(BOLD);
  fill(255);
  text(score, width / 2, 20);
}

//--------------------------------- CLIQUE ------------------------------------------
function mousePressed() {
  oiseau.vitesseY = oiseauImpulsion;
}

//--------------------------------- AFFICHER TUYAUX ------------------------------------------
function afficherTuyaux(t) {
  image(imageTuyauHaut, t.x, t.y - imageTuyauHaut.height);
  image(imageTuyauBas, t.x, t.y + tuyauEspace);
}

//--------------------------------- COLLISION ------------------------------------------
function VerifierCollision(t) {
  // Collision avec le tuyau du haut
  if (
    oiseau.x + imageOiseau.width > t.x &&
    oiseau.x < t.x + imageTuyauHaut.width &&
    oiseau.y < t.y
  ) {
    return true;
  }

  // Collision avec le tuyau du bas
  if (
    oiseau.x + imageOiseau.width > t.x &&
    oiseau.x < t.x + imageTuyauBas.width &&
    oiseau.y + imageOiseau.height > t.y + tuyauEspace
  ) {
    return true;
  }

  // Collision avec le haut de l'écran
  if (oiseau.y < 0) {
    return true;
  }

  // Collision avec le bas de l'écran
  if (oiseau.y + imageOiseau.height > height) {
    return true;
  }

  // Pas de collision
  return false;
}`}
      </CodeBlock>
    </>
  );
};

export default Chapitre7Content; 